import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Datagrid,
  FunctionField,
  List,
  ListActions,
  ListContextProvider,
  NumberField,
  ReferenceField,
  SelectField,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useList,
} from "react-admin";
import { Box, Tab, Tabs, Typography, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  CAMPAIGNS,
  DormitoryCampaignApplicantTypes,
  DormitoryCampaignStatuses,
  DormitoryServicePath,
  MOBILE_BREAKPOINT,
  STUDENT_CATEGORIES,
} from "utils/constants";
import { DormitoryCampaignStatusChoices } from "utils/choices";
import { settings } from "app";
import permissions from "app/permissions";
import { MobileListRow } from "features/MobileListRow";
import { SectionTitle } from "features/SectionTitle";
import SecuredComponent from "server/security/SecuredComponent";
import { administrationRequests } from "api/administrationApi";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import ApplicationDetailsModal from "./ApplicationDetailsModal";
import CheckInModal from "./CheckInModal";
import ReviewApplicationModal from "./ReviewApplicationModal";
import StudentCategoryTabs from "../Partials/StudentCategoryTabs";
import DatagridActions from "../Partials/DatagridActions";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";

// CampaignApplicationStatus, numbered in the backend: Submitted=1, Approved=2, Expired=3, Rejected=4
// (appended after Expired, so the ordinals are not consecutive with the tab order below).
export const ACCOMMODATION_TABS = {
  APPROVED: "approved",
  EXPIRED: "expired",
  REJECTED: "rejected",
  UNPROCESSED: "unprocessed",
};

const TAB_CONFIG = {
  [ACCOMMODATION_TABS.APPROVED]: {
    label: "Одобрени молби",
    status: 2,
  },
  [ACCOMMODATION_TABS.REJECTED]: {
    label: "Неодобрени молби",
    status: 4,
  },
  [ACCOMMODATION_TABS.EXPIRED]: {
    label: "Изтекли молби",
    status: 3,
  },
  [ACCOMMODATION_TABS.UNPROCESSED]: {
    label: "Необработени молби",
    status: 1,
  },
};

const CAMPAIGNS_PER_PAGE = 500;
const CAMPAIGN_GROUPS = {
  CONFIRMED: "confirmed",
  DRAFT: "draft",
  FINISHED: "finished",
  CANCELLED: "cancelled",
};

const renderStudentFullName = record => record?.studentFullName || "—";
const renderPin = record => record?.personId || "—";
// A transfer application asks to move an existing resident to a new room; a plain application is a
// fresh accommodation. The kind is marked so staff know they are approving a destination for a move.
const renderApplicationType = record => (record?.isTransfer ? "Преместване" : "Настаняване");
const renderApplicationPeriod = record =>
  `${formatDateOnlyBg(record?.documentsFrom)} – ${formatDateOnlyBg(record?.documentsTo)}`;
const renderAccommodationPeriod = record =>
  `${formatDateOnlyBg(record?.accommodationFrom)} – ${formatDateOnlyBg(record?.accommodationTo)}`;
const renderAssignedRoom = record => {
  if (!record?.assignedResidentialNumber || !record?.assignedRoomNumber) return "—";
  const building = String(record.assignedResidentialNumber).replace(/^Блок\s*/i, "");
  return `${building}-${record.assignedRoomNumber}`;
};

const CampaignSelection = ({ campaigns, isMobile, loading, onOpen }) => {
  const [campaignGroup, setCampaignGroup] = useState(CAMPAIGN_GROUPS.CONFIRMED);
  const groupedCampaigns = useMemo(() => {
    if (campaignGroup === CAMPAIGN_GROUPS.CONFIRMED) {
      return campaigns.filter(campaign => campaign.status === DormitoryCampaignStatuses.Confirmed);
    }

    if (campaignGroup === CAMPAIGN_GROUPS.DRAFT) {
      return campaigns.filter(campaign => campaign.status === DormitoryCampaignStatuses.Draft);
    }

    if (campaignGroup === CAMPAIGN_GROUPS.FINISHED) {
      return campaigns.filter(campaign => campaign.status === DormitoryCampaignStatuses.Finished);
    }

    return campaigns.filter(campaign => campaign.status === DormitoryCampaignStatuses.Cancelled);
  }, [campaignGroup, campaigns]);
  const listContext = useList({ data: groupedCampaigns });

  if (loading) {
    return <Typography sx={{ py: 4, textAlign: "center" }}>Зареждане ...</Typography>;
  }

  if (campaigns.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        Няма кампании за избраната категория.
      </Typography>
    );
  }

  const openButton = record => (
    <Button
      label="Преглед на молби"
      onClick={() => onOpen(record)}
    >
      <VisibilityIcon />
    </Button>
  );

  return (
    <>
      <Tabs value={campaignGroup} onChange={(_, value) => setCampaignGroup(value)}>
        <Tab value={CAMPAIGN_GROUPS.CONFIRMED} label="Потвърдени" />
        <Tab value={CAMPAIGN_GROUPS.DRAFT} label="Чернови" />
        <Tab value={CAMPAIGN_GROUPS.FINISHED} label="Приключени" />
        <Tab value={CAMPAIGN_GROUPS.CANCELLED} label="Отказани" />
      </Tabs>
      <ListContextProvider value={listContext}>
        {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => <MobileListRow fields={[{ label: "Кампания", value: record?.nameBg }]} />}
          secondaryText={record => (
            <div>
              <MobileListRow
                fields={[{ label: "Срок за кандидатстване", value: renderApplicationPeriod(record) }]}
              />
              <MobileListRow
                fields={[{ label: "Период за настаняване", value: renderAccommodationPeriod(record) }]}
              />
              <MobileListRow
                fields={[{ label: "Брой молби", value: <NumberField source="applicationsCount" /> }]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Статус",
                    value: <SelectField source="status" choices={DormitoryCampaignStatusChoices} />,
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={openButton}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
        >
          <TextField
            source="nameBg"
            label="Кампания"
            sortable={false}
          />
          <FunctionField
            source="applicationPeriod"
            label="Срок за кандидатстване"
            render={renderApplicationPeriod}
            sortable={false}
          />
          <FunctionField
            source="accommodationPeriod"
            label="Период за настаняване"
            render={renderAccommodationPeriod}
            sortable={false}
          />
          <NumberField source="applicationsCount" label="Брой молби" sortable={false} />
          <SelectField
            source="status"
            label="Статус"
            choices={DormitoryCampaignStatusChoices}
            sortable={false}
          />
          <FunctionField
            source="applicationsAction"
            label={false}
            render={record => <DatagridActions>{openButton(record)}</DatagridActions>}
            sortable={false}
          />
        </Datagrid>
        )}
      </ListContextProvider>
    </>
  );
};

const ListAccommodations = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const notifyError = useNotifyError();
  const [studentCategory, setStudentCategory] = useState(STUDENT_CATEGORIES.INTERNAL);
  const [activeTab, setActiveTab] = useState(ACCOMMODATION_TABS.APPROVED);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const isExternal = selectedCampaign?.applicantType === DormitoryCampaignApplicantTypes.External;
  const applicantType = studentCategory === STUDENT_CATEGORIES.EXTERNAL
    ? DormitoryCampaignApplicantTypes.External
    : DormitoryCampaignApplicantTypes.Internal;
  const visibleCampaigns = useMemo(
    () => campaigns.filter(campaign => campaign.applicantType === applicantType),
    [applicantType, campaigns],
  );

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const { data } = await dataProvider.getList(`${DormitoryServicePath}${CAMPAIGNS}`, {
        pagination: { page: 1, perPage: CAMPAIGNS_PER_PAGE },
        sort: { field: "id", order: "DESC" },
        filter: {},
      });
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      setCampaigns([]);
      notifyError(err);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [notifyError]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const openCampaign = campaign => {
    setActiveTab(ACCOMMODATION_TABS.APPROVED);
    setSelectedCampaign(campaign);
  };

  // Which actions a row offers is the backend's call, not the tab's: canReview and canCheckIn already
  // fold in the campaign status, the application status and - for check-in - the assigned room, the
  // absent accommodation and an unexpired appearance deadline. Reading them off the row rather than
  // off activeTab is what stops a page left open before a campaign was cancelled from still offering
  // approval. Both must be exactly true; a row from a backend that does not send them yet offers
  // nothing. Details stay available in every state, so history remains readable throughout.
  const renderActions = record => (
    <>
      <Box>
        <ApplicationDetailsModal record={record} isExternal={isExternal} />
      </Box>
      {record?.canReview === true && (
        <SecuredComponent permission={permissions.canManageDormitoryCampaigns}>
          <Box>
            <ReviewApplicationModal record={record} isExternal={isExternal} />
          </Box>
        </SecuredComponent>
      )}
      {record?.canCheckIn === true && (
        <SecuredComponent permission={permissions.canManageDormitoryAccommodations}>
          <Box>
            <CheckInModal record={record} />
          </Box>
        </SecuredComponent>
      )}
    </>
  );

  const renderApplicationStatus = record => {
    if (activeTab === ACCOMMODATION_TABS.EXPIRED) return "Изтекла";
    if (activeTab === ACCOMMODATION_TABS.REJECTED) return "Неодобрена";
    if (activeTab === ACCOMMODATION_TABS.UNPROCESSED) return "Необработена";
    return record?.hasAccommodation ? "Настанен" : "Одобрена";
  };

  return (
    <Box sx={{ mt: 1 }}>
      {!selectedCampaign ? (
        <>
          <Title title="Настанявания" />
          <StudentCategoryTabs
            value={studentCategory}
            onChange={setStudentCategory}
          />
          <CampaignSelection
            campaigns={visibleCampaigns}
            isMobile={isMobile}
            loading={loadingCampaigns}
            onOpen={openCampaign}
          />
        </>
      ) : (
        <>
          <TopToolbar
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
              width: "100%",
              py: 2,
              alignItems: "center",
            }}
          >
            <Button
              label="Назад"
              startIcon={<ArrowBackIcon />}
              onClick={() => setSelectedCampaign(null)}
              color="inherit"
              sx={{ justifySelf: "start" }}
            />
            <SectionTitle
              title={`Кампания: ${selectedCampaign.nameBg}`}
              paddingY={0}
              textAlign="center"
              color="text.primary"
            />
          </TopToolbar>

          <Box sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={(_, newTab) => setActiveTab(newTab)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                value={ACCOMMODATION_TABS.APPROVED}
                label={TAB_CONFIG[ACCOMMODATION_TABS.APPROVED].label}
              />
              <Tab
                value={ACCOMMODATION_TABS.REJECTED}
                label={TAB_CONFIG[ACCOMMODATION_TABS.REJECTED].label}
              />
              <Tab
                value={ACCOMMODATION_TABS.EXPIRED}
                label={TAB_CONFIG[ACCOMMODATION_TABS.EXPIRED].label}
              />
              <Tab
                value={ACCOMMODATION_TABS.UNPROCESSED}
                label={TAB_CONFIG[ACCOMMODATION_TABS.UNPROCESSED].label}
              />
            </Tabs>
          </Box>

          <List
            key={`${selectedCampaign.id}-${activeTab}`}
            resource={`${DormitoryServicePath}campaigns/${selectedCampaign.id}/applications`}
            title="Настанявания"
            exporter={false}
            filter={{
              status: TAB_CONFIG[activeTab].status,
              isExternal,
            }}
            actions={<ListActions hasCreate={false} />}
          >
            {isMobile ? (
              <SimpleList
                linkType={false}
                sx={settings.style.mobileList}
                primaryText={record => (
                  <MobileListRow
                    fields={[
                      { label: "Име", value: renderStudentFullName(record) },
                      ...(!isExternal ? [{ label: "Факултетен №", value: record?.facultyNumber || "—" }] : []),
                    ]}
                  />
                )}
                secondaryText={record => (
                  <div>
                    <MobileListRow fields={[{ label: "ЕГН", value: renderPin(record) }]} />
                    <MobileListRow fields={[{ label: "Вид молба", value: renderApplicationType(record) }]} />
                    {isExternal && (
                      <MobileListRow
                        fields={[
                          {
                            label: "Университет",
                            value: (
                              <ReferenceField
                                source="originUniversityId"
                                reference={administrationRequests.getUniversitiesSelect()}
                                link={false}
                                emptyText="—"
                              >
                                <TextField source="name" />
                              </ReferenceField>
                            ),
                          },
                        ]}
                      />
                    )}
                    {[ACCOMMODATION_TABS.APPROVED, ACCOMMODATION_TABS.EXPIRED].includes(activeTab) && (
                      <MobileListRow fields={[{ label: "Стая", value: renderAssignedRoom(record) }]} />
                    )}
                    {activeTab === ACCOMMODATION_TABS.EXPIRED && (
                      <MobileListRow
                        fields={[
                          {
                            label: "Краен срок за явяване",
                            value: formatDateOnlyBg(record?.appearanceDeadline),
                          },
                        ]}
                      />
                    )}
                    {activeTab !== ACCOMMODATION_TABS.APPROVED && (
                      <MobileListRow
                        fields={[{ label: "Подадена на", value: formatDateOnlyBg(record?.createdOn) }]}
                      />
                    )}
                    <MobileListRow fields={[{ label: "Статус", value: renderApplicationStatus(record) }]} />
                  </div>
                )}
                tertiaryText={record => renderActions(record)}
              />
            ) : (
              <Datagrid
                bulkActionButtons={false}
                rowClick={false}
              >
                {!isExternal && (
                  <TextField
                    source="facultyNumber"
                    label="Факултетен №"
                    emptyText="—"
                    sortable={false}
                  />
                )}
                <FunctionField
                  source="studentFullName"
                  label="Имена"
                  render={renderStudentFullName}
                  sortable={false}
                />
                <FunctionField
                  source="personId"
                  label="ЕГН"
                  render={renderPin}
                  sortable={false}
                />
                <FunctionField
                  source="applicationType"
                  label="Вид молба"
                  render={renderApplicationType}
                  sortable={false}
                />
                {isExternal && (
                  <ReferenceField
                    source="originUniversityId"
                    reference={administrationRequests.getUniversitiesSelect()}
                    label="Университет"
                    link={false}
                    emptyText="—"
                    sortable={false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                )}
                {[ACCOMMODATION_TABS.APPROVED, ACCOMMODATION_TABS.EXPIRED].includes(activeTab) && (
                  <FunctionField
                    source="assignedRoom"
                    label="Стая"
                    render={renderAssignedRoom}
                    sortable={false}
                  />
                )}
                {activeTab === ACCOMMODATION_TABS.EXPIRED && (
                  <FunctionField
                    source="appearanceDeadline"
                    label="Краен срок за явяване"
                    render={record => formatDateOnlyBg(record?.appearanceDeadline)}
                    sortable={false}
                  />
                )}
                {activeTab !== ACCOMMODATION_TABS.APPROVED && (
                  <FunctionField
                    source="createdOn"
                    label="Подадена на"
                    render={record => formatDateOnlyBg(record?.createdOn)}
                    sortable={false}
                  />
                )}
                <FunctionField
                  source="displayStatus"
                  label="Статус"
                  render={renderApplicationStatus}
                  sortable={false}
                />
                <FunctionField
                  source="actions"
                  label={false}
                  sortable={false}
                  render={record => <DatagridActions>{renderActions(record)}</DatagridActions>}
                />
              </Datagrid>
            )}
          </List>
        </>
      )}
    </Box>
  );
};

export default ListAccommodations;
